import React, { ReactNode } from "react";
import Theme from "@adsk/alloy-react-theme";
import Button from "@adsk/alloy-react-button";
import { ButtonHTMLAttributes } from "react";
import { ConditionalTooltip } from "@adsk/acc-issues-common/components/primitive/ConditionalTooltip";
import { CSSObject } from "styled-components";
import { useTranslations } from "@adsk/acc-issues-translations";
import { TrashCanIcon } from "@adsk/alloy-react-icon";

/**
 * Adds the style and text for a delete button, but allows overriding any props
 */
export const DeleteButton = ({
  style,
  disabled,
  disabledReason,
  tooltipContainer,
  shouldBeSkinny,
  label,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  disabledReason?: ReactNode;
  tooltipContainer?: any;
  shouldBeSkinny?: boolean;
  label?: ReactNode;
}) => {
  const { T } = useTranslations();
  const buttonText = label ? label : T.translate("actions.delete");
  const hasDisabledTooltip = Boolean(disabled && disabledReason);
  return (
    <ConditionalTooltip
      style={{ maxWidth: 300 }}
      condition={hasDisabledTooltip}
      container={tooltipContainer}
      content={disabledReason!}
    >
      <ConditionalTooltip
        // if the button should be skinny, we show a tooltip with the buttonText
        // as long as it's not disabled. If it's disabled, the other tooltip takes precedence.
        condition={Boolean(!hasDisabledTooltip && shouldBeSkinny)}
        container={tooltipContainer}
        content={buttonText}
      >
        <Button
          data-testid="delete-button"
          // if style is undefined, this all still works.
          // but if it is passed, we don't want to override the color
          style={
            disabled
              ? (style as CSSObject)
              : { ...style, color: Theme.colors.red700 }
          }
          variant={Button.VARIANTS.TERTIARY}
          renderIcon={p => <TrashCanIcon {...p} />}
          disabled={disabled}
          {...props}
        >
          {
            // if the button is skinny, we only show the icon
          }
          {!shouldBeSkinny && buttonText}
        </Button>
      </ConditionalTooltip>
    </ConditionalTooltip>
  );
};
